import SelectInputForm from '@repo/ui/components/forms/SelectInputForm';
import TextInputForm from '@repo/ui/components/forms/TextInputForm';
import { useCallback, useEffect, useState, useMemo } from 'react';
import CustomerModal from '../../Modal/CustomerModal';
import AddService from './AddService';
import ServiceRequestModal from '@/features/services/components/Modal/ServiceModal';
import { FetchDataOptions } from '@/types/table.types';
import { apiGet } from '@repo/lib/axios/axios-utils';
import { endpoints } from '@/lib/axios/axios-global';
import { useFormContext, useWatch } from 'react-hook-form';
import { Branch } from '@/types/branches.types';
import { Services } from '@/types/services.types';
import { Employee } from '@/types/employee.types';

export default function ServiceInfo() {
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
    const [dataBranch, setDataBranch] = useState<Branch[]>([]);
    const [dataServices, setDataServices] = useState<Services[]>([]);
    const [dataEmployee, setDataEmployee] = useState<Employee[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { watch, setValue, control } = useFormContext(); // Added setValue to programmatically set paid field

    const openServiceModal = () => setIsServiceModalOpen(true);
    const closeServiceModal = () => setIsServiceModalOpen(false);
    const openCustomerModal = () => setIsCustomerModalOpen(true);
    const closeCustomerModal = () => setIsCustomerModalOpen(false);
    const closeBranchModal = () => setIsBranchModalOpen(false);

    // Monitor form values and update paid field when service changes
    const selectedServiceId = watch('service_id');
    useEffect(() => {
        if (selectedServiceId) {
            const selectedService = dataServices.find((service) => String(service.id) === selectedServiceId);
            if (selectedService) {
                setValue('paid', selectedService.price || 0); // Set the paid field to the service price
            }
        }
    }, [selectedServiceId, dataServices, setValue]);

    // Fetch branches
    const fetchBranches = useCallback(async (options: FetchDataOptions) => {
        setIsLoading(true);
        try {
            const response = await apiGet({
                endpoint: endpoints.branch.select,
                queryParams: {
                    page: options.pageIndex + 1,
                    ...options.filters,
                    search: options.globalFilter,
                },
            });
            const branches = response.data.data?.data || response.data.data || [];
            setDataBranch(branches);
        } catch (error) {
            console.error('Error fetching branches:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch services
    const fetchServices = useCallback(async (options: FetchDataOptions) => {
        setIsLoading(true);
        try {
            const response = await apiGet({
                endpoint: endpoints.courses.select,
                queryParams: {
                    page: options.pageIndex + 1,
                    ...options.filters,
                    search: options.globalFilter,
                },
            });
            const services = response.data.data?.data || response.data.data || [];
            setDataServices(services);
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch employees
    const fetchEmployee = useCallback(async (options: FetchDataOptions) => {
        setIsLoading(true);
        try {
            const response = await apiGet({
                endpoint: endpoints.student.select,
                queryParams: {
                    page: options.pageIndex + 1,
                    ...options.filters,
                    search: options.globalFilter,
                },
            });
            const employees = response.data.data?.data || response.data.data || [];
            setDataEmployee(employees);
        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch data on component mount
    useEffect(() => {
        fetchBranches({ pageIndex: 0, filters: {}, globalFilter: '' });
        fetchServices({ pageIndex: 0, filters: {}, globalFilter: '' });
        fetchEmployee({ pageIndex: 0, filters: {}, globalFilter: '' });
    }, [fetchBranches, fetchServices, fetchEmployee]);

    // Transform branch data to options
    const branchOptions = dataBranch.map((branch) => ({
        name: branch.name,
        value: String(branch.id),
    }));

    // Transform service data to options (remove price from dropdown)
    const servicesOptions = dataServices.map((service) => ({
        name: service.name_ar, // Only show service name
        value: String(service.id),
    }));

    // Transform employee data to options
    const customerOptions = dataEmployee.map((employee) => ({
        name: employee.name,
        value: String(employee.id),
    }));

    const options = [
        { name: 'جديد', value: 'pending' },
        { name: 'قيد المتابعة', value: 'in_progress' },
        { name: 'مكتمل', value: 'completed' },
        { name: 'غير مكتمل', value: 'uncompleted' },
        { name: 'ملغي', value: 'cancelled' },
    ];

    const from = [
        { name: 'Facebook', value: 'facebook' },
        { name: 'LinkedIn', value: 'linkedIn' },
        { name: 'X', value: 'x' },
        { name: 'Instagram', value: 'instagram' },
        { name: 'Website', value: 'website' },
    ];

    // Get the selected service price
    const selectedService = dataServices.find((service) => String(service.id) === selectedServiceId);
    const servicePrice = selectedService ? selectedService.price || 0 : 0;

    // Watch the paid field to calculate remaining (using useWatch for real-time updates)
    const paidValue = useWatch({ name: 'paid' as any, control });

    // Calculate remaining: المدفوع - السعر
    const remaining = useMemo(() => {
        const paid = parseFloat(String(paidValue || 0)) || 0;
        const price = parseFloat(String(servicePrice || 0)) || 0;
        const result = paid - price;
        return Math.max(0, isNaN(result) ? 0 : result);
    }, [paidValue, servicePrice]);

    // Update the remaining field in the form whenever it changes
    useEffect(() => {
        setValue('remaining' as any, remaining, { shouldValidate: false, shouldDirty: false });
    }, [remaining, setValue]);

    return (
        <>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <AddService
                    openModal={openCustomerModal}
                    options={customerOptions}
                    label="اسم الطالب"
                    placeholder="محمد الخالدي"
                    name="customer_id"
                    title="اضافة طالب"
                />
                <AddService
                    openModal={openServiceModal}
                    options={servicesOptions}
                    label="نوع الدوره"
                    placeholder="ادخل نوع الدوره"
                    name="service_id"
                    title="اضافة طلب دوره"
                />
            </div>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <TextInputForm
                    name="paid"
                    label="المدفوع"
                    placeholder="المدفوع"
                // disabled // Make the field read-only
                />

                {selectedServiceId && (
                    <div className="flex flex-col gap-4">
                        السعر:
                        <button
                            type="button"
                            className="px-4 py-2 bg-blue-100/80 text-[#000] rounded-md h-fit text-start text-sm"
                            disabled
                        >
                            {servicePrice}
                        </button>
                    </div>
                )}

                <TextInputForm
                    name="remaining"
                    label="الباقي"
                    placeholder="الباقي"
                    disabled
                />
            </div>


            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectInputForm name="from" label="المصدر" placeholder="9:00 PM" options={from} />
                <SelectInputForm name="status" label="حالة الطلب" placeholder="جديد" options={options} />
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6">
                <TextInputForm name="notes" label="ملاحظات" placeholder="ملاحظات بسيطة" />
            </div>

            {isServiceModalOpen && <ServiceRequestModal isOpen={isServiceModalOpen} onClose={closeServiceModal} />}
            {isCustomerModalOpen && <CustomerModal isOpen={isCustomerModalOpen} onClose={closeCustomerModal} />}
        </>
    );
}
